/**
 * Branded customer-facing quote document — a faithful port of the CSR app's
 * `buildQuote()` (logo, QR, faux barcode, client block, specs, pricing, terms).
 * Returns an HTML string; render inside `<div class="quote mfqwrap">` with
 * QUOTE_DOC_CSS injected. Excludes all internal cost/margin data by design.
 */
import { LOGO_LIGHT, QR_URI, QUOTE_DISCLAIMER } from './brandAssets';
import {
  MODES, skuCostModel, skuCellUnit, money, unitMoney, intfmt,
  type QuoteState, type QuoteResult, type EngineMode,
} from './quoteEngine';
import type { Customer } from './csrSuite';

const esc = (v: unknown): string =>
  String(v == null ? '' : v).replace(/[&<>"]/g, (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[m] as string));

// Local calendar day (never toISOString — that's the UTC day and drifts after 5pm PT).
const localKey = (d: Date): string =>
  d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');

function addDays(dstr: string, n: number): string {
  const d = new Date((dstr || localKey(new Date())) + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return localKey(d);
}
function fmtLong(dstr: string): string {
  if (!dstr) return '—';
  const d = new Date(dstr + 'T00:00:00');
  if (isNaN(d.getTime())) return dstr;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function fauxBarcode(str: string): string {
  let bars = '';
  const s = (str || 'MFX') + '||';
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    for (let b = 0; b < 3; b++) {
      const w = 1 + ((c >> b) & 3);
      const h = 70 + ((c * (b + 3)) % 30);
      bars += `<i style="width:${w}px;height:${h}%"></i>`;
    }
  }
  return `<div class="barcode">${bars}</div>`;
}

export interface QuoteDocOptions { skuMode?: boolean; skuMax?: number; dateStr?: string; }

export function buildQuoteDocHtml(s: QuoteState, r: QuoteResult, mode: EngineMode, cust: Customer | undefined, opts: QuoteDocOptions = {}): string {
  const c = cust || ({} as Customer);
  const rows = r.rows.filter((x) => x.q > 0);
  const dateStr = opts.dateStr || localKey(new Date());
  const validUntil = addDays(dateStr, 30);
  const lead = MODES[mode].lead;
  let best: (typeof rows)[number] | null = null;
  rows.forEach((row) => { if (!best || row.mfxUnit < best.mfxUnit) best = row; });
  const bestRow = best as (typeof rows)[number] | null;
  const jobDesc = `${s.sizeAcross || '—'}"×${s.sizeAround || '—'}" ${s.shape || ''} — ${s.colors || 0}C ${s.process || ''}`.trim();
  const chip = (v: unknown) => `<span class="chip">${esc(v == null || v === '' ? 'TBD' : v)}</span>`;
  const kv = (k: string, v: unknown) => `<div class="mr"><span class="k">${k}</span><span class="v">${v == null || v === '' ? '—' : esc(v)}</span></div>`;
  const cf = (l: string, v: unknown, sm?: boolean) => `<div class="cfield"><div class="mlbl">${l}</div><div class="v ${sm ? 'sm' : ''}">${v == null || v === '' ? '—' : esc(v)}</div></div>`;

  const priceRows = rows.map((row) => `<tr>
      <td>${intfmt(row.q)}</td>
      <td><span class="up">${unitMoney(row.mfxUnit)}</span><br><span class="ex">${money(row.mfxTotal)}</span></td>
    </tr>`).join('') || `<tr><td colspan="2" style="text-align:center;color:#889">Enter quantities to price</td></tr>`;

  const useSku = !!opts.skuMode && rows.length > 0;
  let pricingBlock: string;
  if (useSku) {
    const m = skuCostModel(s, r);
    const K = Math.max(1, Math.min(10, opts.skuMax || 3));
    const tiers = r.rows.map((row, i) => ({ i, q: row.q })).filter((x) => x.q > 0);
    const header = `<tr><th class="skc">Qty</th>${Array.from({ length: K }, (_, j) => `<th>${j + 1} SKU${j ? 's' : ''}</th>`).join('')}</tr>`;
    const brows = tiers.map((tr) => `<tr><td class="skc">${intfmt(tr.q)}</td>${Array.from({ length: K }, (_, j) => {
      const N = j + 1;
      const unit = skuCellUnit(N, tr.q, m);
      return `<td><b>${unitMoney(unit)}</b><span class="exx">${money(unit * tr.q)}</span></td>`;
    }).join('')}</tr>`).join('');
    pricingBlock = `<div class="mprice">
      <div class="stitle">Pricing <span style="color:#9aa;font-weight:600;text-transform:none;letter-spacing:0">· per-unit price / extended · up to ${K} SKU${K > 1 ? 's' : ''} · ${MODES[mode].label}</span></div>
      <table class="mpt skuqt"><thead>${header}</thead><tbody>${brows}</tbody></table>
    </div>`;
  } else {
    pricingBlock = `<div class="mprice">
      <div class="stitle">Pricing <span style="color:#9aa;font-weight:600;text-transform:none;letter-spacing:0">· ${s.copies} SKU${s.copies > 1 ? 's' : ''} · ${MODES[mode].label}</span></div>
      <table class="mpt">
        <thead><tr><th>Qty</th><th>Price / Unit &nbsp;·&nbsp; Extended</th></tr></thead>
        <tbody>${priceRows}</tbody>
      </table>
    </div>`;
  }
  const skuCountMeta = useSku ? ('up to ' + Math.max(1, Math.min(10, opts.skuMax || 3))) : s.copies;
  const terms = [
    `Quote valid for 30 days from date of issue.`,
    `Subject to artwork review and approval.`,
    `Plates remain property of Microflex Film Corp until paid in full.`,
    `Lead time is ${lead} working days from proof sign off.`,
    `Quantities shipped are ±10% of order quantity.`,
    `For stand up gusset bags, quantity tolerance is ±20%.`,
    `Stability testing must be done prior to running order.`,
    `Material approval form must be signed after testing is concluded.`,
    `Art will be billed at $90.00/hour if art is not in AI format.`,
  ];

  return `<div class="mfq">
    <div class="mhead">
      <div class="hlogo"><img class="mfxlogo" alt="Microflex Film Corporation" src="${LOGO_LIGHT}"></div>
      <div class="hqr"><img src="${QR_URI}" alt="Scan · Connect"></div>
      <div class="tag"><b>Flexible Packaging. Engineered to Perform.</b>MicroflexFilm.com · (909) 360-9066 · Riverside, California</div>
    </div>
    <div class="mcap"><span>Flexible Packaging</span><span>Labels</span><span>Pouches</span><span>Shrink Sleeves</span><span>Sachets</span><span>Stick Packs</span></div>
    <div class="mtop">
      <div class="mclient">
        <div class="mlbl">Client</div>
        <div class="cbig">${esc(s.company || '—')}</div>
        <div class="cgrid">
          ${cf('Contact', s.attention)}
          ${cf('Email', s.phone && s.phone.includes('@') ? s.phone : (c.email || '—'), true)}
          ${cf('Client Code', c.code || 'MFX-000-00')}
          ${cf('Industry', (c as Record<string, string | undefined>).industry || '—')}
          ${cf('City, State', [(c as Record<string, string | undefined>).city, c.bst || c.sst].filter(Boolean).join(', ') || '—')}
          ${cf('Terms', c.tm || 'Net 30')}
        </div>
      </div>
      <div class="mright">
        ${fauxBarcode(s.quoteNo || 'MFX')}
        <div class="bc-no">${esc(s.quoteNo || '—')}</div>
        <div class="qnum">${esc(s.quoteNo || 'QUOTE')}</div>
        <div class="rev">REV A</div>
      </div>
    </div>

    <div class="mmeta">
      ${kv('Date', fmtLong(dateStr))}
      ${kv('Job Description', jobDesc)}
      ${kv('SKUs', skuCountMeta)}
      ${kv('Qty Recommendation', bestRow ? intfmt(bestRow.q) + ' (best value)' : '—')}
      ${kv('Ship To', s.freight || '—')}
      ${kv('Lead Time', lead + ' working days')}
      ${kv('Valid Until', fmtLong(validUntil))}
    </div>

    <div class="mteam">
      <div class="tl">Flex Team</div>
      <div class="tr"><span class="k">Estimator</span><span class="v">${esc(s.estimator || '—')}</span></div>
      <div class="tr"><span class="k">Sales Rep</span><span class="v">${esc(s.salesRep || '—')}</span></div>
    </div>

    ${s.addNotes ? `<div class="mnotes"><div class="tl">Notes &amp; Special Instructions</div><div class="tx">${esc(s.addNotes)}</div></div>` : ''}

    <div class="msec">
      <div class="stitle">Specifications</div>
      <div class="specbox dim"><span class="sgl">Dimensions</span>
        <span class="sk">Size</span>${chip((s.sizeAcross || '?') + '" × ' + (s.sizeAround || '?') + '"')}
        <span class="sk">Shape</span>${chip(s.shape)}
        <span class="sk">Colors</span>${chip(s.colors)}
        <span class="sk">Copies/SKUs</span>${chip(s.copies)}</div>
      <div class="specbox fin"><span class="sgl">Finishing Specs</span>
        <span class="sk">Web Width</span>${chip((Math.round(r.webWidth * 1000) / 1000) + '"')}
        <span class="sk"># Across</span>${chip(s.nAcross)}
        <span class="sk">Repeat</span>${chip(r.repeat)}
        <span class="sk">Die #</span>${chip(s.die)}
        <span class="sk">Plates</span>${chip(r.plates)}</div>
      <div class="specbox mat"><span class="sgl">Materials</span>
        <span class="sk">Face</span>${chip(s.faceStock)}
        <span class="sk">Lam</span>${chip(s.laminate && s.laminate !== '0' ? s.laminate : 'NA')}
        <span class="sk">Zip</span>${chip(s.zipper ? s.zipper : 'NA')}
        <span class="sk">Liner</span>${chip(s.liner && s.liner !== '0' ? s.liner : 'NA')}
        <span class="sk">Adhesive</span>${chip(s.adhesive && s.adhesive !== '0' ? s.adhesive : 'NA')}
        <span class="sk">Coating</span>${chip(s.coating && s.coating !== 'NONE' ? s.coating : 'NA')}</div>
    </div>

    ${pricingBlock}

    <div class="mterms">
      <div class="th"><span>Terms &amp; Conditions</span><span class="r">${esc(c.tm || 'Net 30')}</span></div>
      <ul>${terms.map((t) => `<li>${esc(t)}</li>`).join('')}</ul>
    </div>

    ${QUOTE_DISCLAIMER ? `<div class="mdisc"><b>Notes:</b> ${esc(QUOTE_DISCLAIMER)}</div>` : ''}

    <div class="mfoot">
      <div class="h">Ready to get started?</div>
      <div class="s">Claim this pricing — contact us to place your order</div>
      <div class="c"><span>MicroflexFilm.com</span>|<span>(909) 360-9066</span>|<span>Quotes@MicroflexFilm.com</span></div>
    </div>
  </div>`;
}
